/**
 * Builds a high-quality PNG share card from an analysis result.
 * Returns a Promise<Blob> with the PNG image.
 */

const W = 1080;
const H = 1080;

const VERDICT_COLORS = {
  IA: { color: "#ef4444", label: "HECHO CON IA", glow: "rgba(239,68,68,0.45)" },
  REAL: { color: "#22c55e", label: "VIDEO REAL", glow: "rgba(34,197,94,0.45)" },
  INCIERTO: { color: "#f59e0b", label: "INCIERTO", glow: "rgba(245,158,11,0.45)" },
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let current = "";
  for (const w of words) {
    const test = current ? current + " " + w : w;
    if (ctx.measureText(test).width > maxWidth) {
      if (current) lines.push(current);
      current = w;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

async function ensureFonts() {
  if (document.fonts && document.fonts.load) {
    try {
      await Promise.all([
        document.fonts.load("900 96px Chivo"),
        document.fonts.load("700 36px Chivo"),
        document.fonts.load("700 48px 'JetBrains Mono'"),
        document.fonts.load("500 24px Inter"),
      ]);
    } catch {
      /* ignore */
    }
  }
}

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export async function buildShareCard(result) {
  await ensureFonts();
  const isotype = await loadImage("/isotype.jpg");

  const meta = VERDICT_COLORS[result.verdict] || VERDICT_COLORS.INCIERTO;
  const confidence = Math.max(0, Math.min(100, result.confidence ?? 0));

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background
  ctx.fillStyle = "#08080b";
  ctx.fillRect(0, 0, W, H);

  // Radial glow top
  const grad = ctx.createRadialGradient(W / 2, 0, 0, W / 2, 0, W * 0.8);
  grad.addColorStop(0, meta.glow);
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Subtle dot pattern
  ctx.fillStyle = "rgba(255,255,255,0.05)";
  for (let y = 24; y < H; y += 32) {
    for (let x = 24; x < W; x += 32) {
      ctx.fillRect(x, y, 1.5, 1.5);
    }
  }

  // Header: isotype logo + brand
  const padX = 64;
  const headerY = 64;
  const logoSize = 76;

  if (isotype) {
    // Draw isotype image directly (already has black bg)
    ctx.save();
    roundRect(ctx, padX, headerY, logoSize, logoSize, 14);
    ctx.clip();
    ctx.drawImage(isotype, padX, headerY, logoSize, logoSize);
    ctx.restore();
    // Subtle border
    roundRect(ctx, padX, headerY, logoSize, logoSize, 14);
    ctx.strokeStyle = "rgba(255,255,255,0.12)";
    ctx.lineWidth = 1;
    ctx.stroke();
  } else {
    // Fallback: white square with "IF"
    roundRect(ctx, padX, headerY, logoSize, logoSize, 14);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.fillStyle = "#08080b";
    ctx.font = "900 32px Chivo, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("IF", padX + logoSize / 2, headerY + logoSize / 2);
  }

  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.font = "800 38px Chivo, sans-serif";
  ctx.fillText("IF Detector", padX + logoSize + 22, headerY + 38);
  ctx.fillStyle = "#a1a1aa";
  ctx.font = "500 18px 'JetBrains Mono', monospace";
  ctx.fillText("DETECTOR DE VIDEO IA", padX + logoSize + 22, headerY + 64);

  // Platform pill
  if (result.source_platform) {
    ctx.font = "600 20px Inter, sans-serif";
    const pillText = result.source_platform.toUpperCase();
    const tw = ctx.measureText(pillText).width;
    const px = W - padX - tw - 36;
    const py = headerY + 18;
    roundRect(ctx, px, py, tw + 36, 36, 18);
    ctx.fillStyle = "rgba(255,255,255,0.08)";
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.18)";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "left";
    ctx.fillText(pillText, px + 18, py + 24);
  }

  // Circular gauge
  const cx = W / 2;
  const cy = 560;
  const radius = 220;
  const stroke = 28;

  // Track
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = stroke;
  ctx.stroke();

  // Glow behind progress
  ctx.shadowColor = meta.color;
  ctx.shadowBlur = 40;
  ctx.beginPath();
  ctx.arc(
    cx,
    cy,
    radius,
    -Math.PI / 2,
    -Math.PI / 2 + (confidence / 100) * Math.PI * 2,
  );
  ctx.strokeStyle = meta.color;
  ctx.lineWidth = stroke;
  ctx.lineCap = "round";
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Percentage text
  ctx.fillStyle = meta.color;
  ctx.font = "900 160px Chivo, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.shadowColor = meta.glow;
  ctx.shadowBlur = 40;
  const pctText = `${confidence}`;
  ctx.fillText(pctText, cx - 28, cy + 50);
  ctx.shadowBlur = 0;

  ctx.font = "900 64px Chivo, sans-serif";
  ctx.fillText("%", cx + ctx.measureText(pctText).width / 2 + 18, cy + 10);

  // Verdict label
  ctx.fillStyle = meta.color;
  ctx.font = "900 72px Chivo, sans-serif";
  ctx.textAlign = "center";
  ctx.shadowColor = meta.glow;
  ctx.shadowBlur = 30;
  ctx.fillText(meta.label, cx, 880);
  ctx.shadowBlur = 0;

  // Summary (if exists, wrap)
  if (result.summary) {
    ctx.fillStyle = "#d4d4d8";
    ctx.font = "500 26px Inter, sans-serif";
    const lines = wrapText(ctx, result.summary, W - padX * 2);
    const maxLines = 2;
    lines.slice(0, maxLines).forEach((line, i) => {
      ctx.fillText(line, cx, 935 + i * 36);
    });
  }

  // Footer
  ctx.fillStyle = "#71717a";
  ctx.font = "600 22px 'JetBrains Mono', monospace";
  ctx.textAlign = "center";
  ctx.fillText("UN SERVICIO DE INTERACTIVA FILMS", cx, H - 48);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("No se pudo generar la imagen"));
      },
      "image/png",
      0.95,
    );
  });
}

export async function downloadShareCard(result) {
  const blob = await buildShareCard(result);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeName = (result.verdict || "resultado").toLowerCase();
  a.href = url;
  a.download = `if-detector-${safeName}-${result.confidence}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
